import React, { useEffect, useState } from 'react';
import { Box, TextField, Button, List, ListItem, ListItemText, Paper, Typography, Divider, Tabs, Tab, Modal } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axios from "axios";
import { useUser } from '../../Context/UserContext';

// --- API Service Imports ---
import { GetApi, PostApi } from '../../Services/api/ApiCall';
import DynamicTable from "../Dynamic/DynamicTable";
import DynamicModel from "../Dynamic/DynamicModal";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


function DataManager({ role }) {
  const [inputValue, setInputValue] = useState('');
  const [items, setItems] = useState(['Engineering', 'Marketing']); // Initial dummy data

  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);


  const [listData, setListData] = useState([]);
  const { token } = useUser();

  const columns = [
    { field: "id", headerName: "ID" },
    { field: "name", headerName: "Name" },
  ]

  const [loading, setLoading] = useState(false);

  const handleAdd = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setItems([...items, inputValue]);
      setInputValue(''); // Clear field after adding
    }
  };

  const fetchData = async () => {

    let response;

    if (role === "Department") {
      response = await GetApi('/officers/departments', token);
      console.log("Response data :", response);
    }
    else if (role === "Designation") {
      response = await GetApi('/officers/designations', token);
      console.log("Response data :", response);
    }
    else {
      response = await GetApi('/users/roles', token);
      console.log("Response data :", response);
    }

    setListData(response);
    console.log("Response set data :", listData);
  }

  const submitData = async () => {
    let response;

    setLoading(true);
    if (role === "Department") {
      response = await PostApi('/officers/departments', { name: inputValue }, token);
      console.log("Response data :", response);
    }
    else if (role === "Designation") {
      response = await PostApi('/officers/designations', { name: inputValue }, token);
      console.log("Response data :", response);
    }
    else {
      response = await PostApi('/users/roles', { name: inputValue }, token);
      console.log("Response data :", response);
    }
    console.log("Response data :", response);

    if (response.id !== null) {
      handleClose();
      setInputValue(''); // Clear input on success
      toast.success(`${role} added successful!`);
      //re-fetch the data
      fetchData();
    }
    else {
      toast.error(`Something went wrong`);
    }
    setLoading(false)
  }

  // Keeping logic consistent with original code, though these seem to copy submitData logic
  const editData = async () => {
    let response;
    setLoading(true);
    if (role === "Department") {
      response = await PostApi('/officers/departments', { name: inputValue }, token);
    }
    else if (role === "Designation") {
      response = await PostApi('/officers/designations', { name: inputValue }, token);
    }
    else {
      response = await PostApi('/users/roles', { name: inputValue }, token);
    }

    if (response.id !== null) {
      handleClose();
      toast.success(`${role} updated successful!`);
      fetchData();
    }
    else {
      toast.error(`Something went wrong`);
    }
    setLoading(false)
  }

  const deleteData = async () => {
    let response;
    setLoading(true);
    if (role === "Department") {
      response = await PostApi('/officers/departments', { name: inputValue }, token);
    }
    else if (role === "Designation") {
      response = await PostApi('/officers/designations', { name: inputValue }, token);
    }
    else {
      response = await PostApi('/users/roles', { name: inputValue }, token);
    }

    if (response.id !== null) {
      handleClose();
      toast.success(`${role} deleted successful!`);
      fetchData();
    }
    else {
      toast.error(`Something went wrong`);
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchData();
  }, [])

  const handleDeleteClick = (row) => { }
  const handleEditClick = (row) => { }

  // Check if current role is "Roles" to hide actions
  const hideActions = role === "Roles";

  return (
    <Box sx={{ 
      mt: 1, 
      backgroundColor: '#e8f7e0', 
      p: { xs: 1, sm: 2 }, // Responsive padding
      borderRadius: 1, 
    }}> 
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <DynamicModel
        open={open}
        onClose={handleClose}
        onConfirm={submitData}
        title={`Add New ${role}`}
        confirmText="Add"
      >          
       <TextField
          fullWidth
          size="small"
          label={`Enter ${role}`}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          autoComplete="off"
          sx={{
            color:"black",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "green", // default
              },
              "&:hover fieldset": {
                borderColor: "green", // hover
              },
              "&.Mui-focused fieldset": {
                borderColor: "green", // focus (optional but recommended)
              },
            },
          }}
        />
      </DynamicModel>

      {/* Form Section - Responsive Header Layout */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        flexDirection: { xs: 'column', sm: 'row' }, // Stack on mobile
        alignItems: { xs: 'flex-start', sm: 'center' }, // Align left on mobile, center on desktop
        mb: { xs: 2, sm: 3 },
        gap: { xs: 1, sm: 0 } // Add gap when stacked
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 800, 
            color: '#1B5E20', 
            fontSize: { xs: '1.5rem', sm: '2rem' },
            // textShadow: '0 2px 6px rgba(0,0,0,0.4)',
            whiteSpace: 'nowrap', // Prevent title wrapping awkwardly
          }}
        >
          {`${role} Management`}
        </Typography>
        
        {role !== "Roles" && (
          <Button
            variant="contained"
            type="submit"
            sx={{ 
              bgcolor: '#1b5e20',
              width: { xs: '100%', sm: 'auto' }, // Full width on mobile
              py: { xs: 1.2, sm: 1 } // Slightly taller button on mobile for touch
            }}
            startIcon={<AddIcon />}
            onClick={handleOpen}
          >
            Add
          </Button>
        )}
      </Box>
      
      {/* Table Section - Responsive Wrapper for horizontal scroll */}
      <Box sx={{ 
        width: '100%', 
        overflowX: 'auto' // Allows table to scroll horizontally on mobile
      }}>
        <DynamicTable 
          data={listData} 
          columns={columns} 
          loading={loading} 
          emptyMessage="No data found" 
          onDelete={hideActions ? undefined : handleDeleteClick}
          onEdit={hideActions ? undefined : handleEditClick}
          hideActions={hideActions}
        />
      </Box>
    </Box>
  );
}


// Helper component for Tab Panel content
function CustomTabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 3 } }}> {/* Responsive padding */}
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function DepartmentTabs() {
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ backgroundColor: '#e8f7e0', minHeight: '100vh', p: { xs: 1, sm: 2 }, borderRadius: 1 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginTop: { xs: 3, sm: 5 } }}>
        <Tabs
          value={value}
          onChange={handleChange}
          centered
          variant="scrollable" 
          scrollButtons="auto"
          aria-label="department roles design tabs"
          sx={{
            // Styles the active text color for all tabs
            '& .Mui-selected': {
              color: '#1b5e20 !important',
              fontSize: '1.1rem', // Slightly smaller font for mobile tabs
              fontWeight: 'bold'
            },
            // Styles the moving line underneath
            '& .MuiTabs-indicator': {
              backgroundColor: '#1b5e20',
              height: 3
            },
            '& .MuiTab-root': {
              fontSize: { xs: '0.9rem', sm: '1.1rem' },
              fontWeight: 600,
              padding: { xs: '8px 10px', sm: '12px 16px' },
              textTransform: 'none',
              minWidth: { xs: 80, sm: 150 },
            }
          }}
        >
          <Tab label="Department" />
          <Tab label="Roles" />
          <Tab label="Designation" />
        </Tabs>
      </Box>
      
      <CustomTabPanel value={value} index={0}>
        <DataManager role={"Department"} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <DataManager role={"Roles"} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <DataManager role={"Designation"} />
      </CustomTabPanel>
    </Box>
  );
}


const EditInfo = () => {
  return <DepartmentTabs />;
}


export default EditInfo;